import React from "react";
import {
  Box, Container, Heading, Text, VStack, SimpleGrid, Button,
  HStack, Icon, Badge
} from "@chakra-ui/react";
import {
  FaHandHoldingUsd, FaCheckCircle, FaArrowRight, FaPhone,
  FaHome, FaPlane, FaHeart, FaGraduationCap, FaMedkit, FaMoneyBillWave
} from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const FINANCE_PRIMARY = "#141C49";
const FINANCE_ACCENT = "#C2A878";
const NAVY = "#141C49";

const useCases = [
  { icon: FaMoneyBillWave, title: "Debt Consolidation", desc: "Combine multiple debts into one manageable repayment at a lower rate. Simplify your finances and save on interest." },
  { icon: FaHome, title: "Home Renovations", desc: "Update the kitchen, add a deck, or renovate the bathroom — without refinancing your home loan." },
  { icon: FaPlane, title: "Travel & Holidays", desc: "That trip you've always wanted doesn't have to wait. Finance it now and repay at a pace that suits you." },
  { icon: FaHeart, title: "Weddings & Events", desc: "Make the moment everything you imagined. Personal loans help you celebrate without compromising." },
  { icon: FaGraduationCap, title: "Education", desc: "Invest in yourself or your family's future. Cover course fees, books, and living costs." },
  { icon: FaMedkit, title: "Medical Expenses", desc: "Health can't wait. Cover procedures, treatments, or unexpected costs with fast, flexible funding." }
];

const FinanceServicesPersonal: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/finance/services/personal"
        fallbackTitle="Personal Loans - One Group Finance"
        fallbackDescription="Flexible personal loans from $3,000 to $80,000. Debt consolidation, home renovations, travel, weddings and more. Funds available within 24 hours of approval."
      />
      <NavbarWithCallToAction />

      {/* Hero */}
      <Box
        position="relative"
        color="white"
        py={{ base: 20, md: 28 }}
        backgroundImage="url('/assets/finance-assets/asset 20.jpeg')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundAttachment={{ base: "scroll", md: "fixed" }}
        _before={{
          content: '""',
          position: "absolute",
          top: 0, left: 0, right: 0, bottom: 0,
          bg: "rgba(0, 31, 63, 0.85)",
          zIndex: 0
        }}
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={6} textAlign="center">
            <Badge bg={FINANCE_ACCENT} color="white" fontSize="sm" px={4} py={1} borderRadius="full">
              Personal Loans
            </Badge>
            <Heading size="2xl" fontWeight="bold" maxW="3xl">
              Finance Built Around You
            </Heading>
            <Text fontSize="xl" color="gray.300" maxW="2xl">
              Let us help bring your plans to life. Personal loans from $3,000 to $80,000 — flexible, competitive, and tailored to your goals.
            </Text>
            <HStack spacing={4} pt={4} flexWrap="wrap" justify="center">
              <Button
                as={RouterLink}
                to="/finance/contact"
                size="lg"
                bg="white"
                color={NAVY}
                rightIcon={<FaArrowRight />}
                _hover={{ bg: FINANCE_ACCENT, color: "white" }}
                transition="all 0.2s"
              >
                Apply in 2 Minutes
              </Button>
              <Button
                size="lg"
                variant="outline"
                borderColor="white"
                color="white"
                leftIcon={<FaPhone />}
                _hover={{ bg: "rgba(255,255,255,0.1)" }}
                transition="all 0.2s"
              >
                1300 536 159
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* Loan Details */}
      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={12} alignItems="center">
            <VStack align="flex-start" spacing={5}>
              <Text fontSize="sm" color={FINANCE_ACCENT} textTransform="uppercase" letterSpacing="wider" fontWeight="600">
                No jargon. Just real help.
              </Text>
              <Heading size="xl" color={NAVY}>
                Personal Loans That Fit Your Life
              </Heading>
              <Text color="gray.600" fontSize="lg" lineHeight="tall">
                Finance can feel overwhelming — but you don't have to do it alone. We partner with Ausloans, Australia's largest privately-owned finance group, giving you access to 40+ lenders and a customer-first approach that puts your goals first.
              </Text>
              <Text color="gray.600" fontSize="lg" lineHeight="tall">
                Whether you need funds for something planned or something unexpected, we find the right rate for your credit profile — and we're with you all the way.
              </Text>
              <SimpleGrid columns={2} spacing={4} w="full">
                {[
                  { stat: "$3K–$80K", label: "Loan Amount" },
                  { stat: "24hrs", label: "Funds Available" },
                  { stat: "40+", label: "Lenders" },
                  { stat: "$0", label: "Early Repayment Fees" }
                ].map((item) => (
                  <Box key={item.stat} p={5} bg="white" borderRadius="xl" shadow="sm" border="1px solid" borderColor="gray.100" textAlign="center">
                    <Text fontSize="2xl" fontWeight="bold" color={NAVY}>{item.stat}</Text>
                    <Text fontSize="sm" color="gray.500">{item.label}</Text>
                  </Box>
                ))}
              </SimpleGrid>
            </VStack>
            <Box
              h="420px"
              borderRadius="2xl"
              overflow="hidden"
              shadow="xl"
              backgroundImage="url('/assets/finance-assets/asset 13.jpeg')"
              backgroundSize="cover"
              backgroundPosition="center"
            />
          </SimpleGrid>
        </Container>
      </Box>

      {/* Use Cases */}
      <Container maxW="container.xl" py={20}>
        <VStack spacing={4} textAlign="center" mb={12}>
          <Heading size="xl" color="gray.800">What Can You Use It For?</Heading>
          <Text fontSize="lg" color="gray.600" maxW="2xl">
            Life doesn't follow a script. Neither should your finance.
          </Text>
        </VStack>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {useCases.map((item, idx) => (
            <Box
              key={idx}
              p={7}
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="xl"
              _hover={{ shadow: "xl", borderColor: FINANCE_ACCENT, transform: "translateY(-4px)" }}
              transition="all 0.3s"
            >
              <Icon as={item.icon} boxSize={10} color={FINANCE_ACCENT} mb={4} />
              <Heading size="sm" mb={3} color="gray.800">{item.title}</Heading>
              <Text color="gray.600" fontSize="sm" lineHeight="tall">{item.desc}</Text>
            </Box>
          ))}
        </SimpleGrid>
      </Container>

      {/* How It Works */}
      <Box bg="gray.50" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={12}>
            <Heading size="xl" color={NAVY}>Three Simple Steps</Heading>
            <Text fontSize="lg" color="gray.600" maxW="xl">From application to funds in as little as 24 hours.</Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
            {[
              {
                number: "01",
                title: "Apply online",
                desc: "Our application takes 2–3 minutes. No credit impact for the initial enquiry. Just tell us what you need and we take it from there."
              },
              {
                number: "02",
                title: "We find your rate",
                desc: "A real person reviews your situation, matches you with the right lender, and presents you with a personalised rate based on your credit profile."
              },
              {
                number: "03",
                title: "Access your funds",
                desc: "Once approved, funds are typically available within 24 hours. Flexible repayment schedules — weekly, fortnightly, or monthly."
              }
            ].map((step) => (
              <Box
                key={step.number}
                p={10}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="xl"
                textAlign="center"
                position="relative"
                _hover={{ shadow: "lg", borderColor: FINANCE_ACCENT }}
                transition="all 0.3s"
              >
                <Box
                  w={12}
                  h={12}
                  bg={FINANCE_ACCENT}
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  mx="auto"
                  mb={5}
                >
                  <Text fontWeight="bold" color="white" fontSize="lg">{step.number}</Text>
                </Box>
                <Heading size="md" mb={3} color={NAVY}>{step.title}</Heading>
                <Text color="gray.600" fontSize="sm" lineHeight="tall">{step.desc}</Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* CTA */}
      <Box bg={NAVY} color="white" py={20}>
        <Container maxW="container.xl" textAlign="center">
          <Heading size="xl" mb={4}>Ready to Bring Your Plans to Life?</Heading>
          <Text fontSize="lg" mb={8} color="gray.300" maxW="xl" mx="auto">
            Speak with our team today — no jargon, no pressure, just real help.
          </Text>
          <HStack justify="center" spacing={4} flexWrap="wrap">
            <Button
              as={RouterLink}
              to="/finance/contact"
              size="lg"
              bg={FINANCE_ACCENT}
              color="white"
              rightIcon={<FaArrowRight />}
              _hover={{ bg: "#a8905f" }}
              transition="all 0.2s"
            >
              Apply Now
            </Button>
            <Button
              size="lg"
              variant="outline"
              borderColor="white"
              color="white"
              leftIcon={<FaPhone />}
              _hover={{ bg: "rgba(255,255,255,0.1)" }}
              transition="all 0.2s"
            >
              1300 536 159
            </Button>
          </HStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default FinanceServicesPersonal;
